import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";

const API = `${window.location.origin}/api`;

export const Appointment = () => {
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_time: "",
    project_type: "",
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/appointments`, {
        ...formData,
        preferred_date: selectedDate.toISOString().split("T")[0],
      });
      toast.success("Appointment request submitted");
      setFormData({
        name: "",
        email: "",
        phone: "",
        preferred_time: "",
        project_type: "",
        notes: "",
      });
      setSelectedDate(undefined);
    } catch (error) {
      toast.error("Failed to submit request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeSlots = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00"];
  const projectTypes = [
    "Residential Architecture",
    "Commercial Architecture",
    "Interior Design",
    "Furniture Design",
    "Consultation",
  ];

  return (
    <div data-testid="appointment-page">
      {/* Header */}
      <header className="page-header-editorial px-16 lg:px-40 bg-[#F5F5F3]" data-testid="appointment-header">
        <div className="max-w-screen-2xl mx-auto">
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-2 text-sm text-[#737373] mb-8 hover:text-[#0A0A0A] transition-colors"
          >
            <ArrowLeft size={16} />
            Back
          </Link>
          <h1 className="page-title-editorial">Book Consultation</h1>
        </div>
      </header>

      {/* Form */}
      <section className="section-editorial px-16 lg:px-40 bg-[#F5F5F3]" data-testid="appointment-form-section">
        <div className="max-w-screen-2xl mx-auto">
          <form onSubmit={handleSubmit} data-testid="appointment-form">
            <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
              {/* Left: Calendar */}
              <div>
                <span className="label-caps block mb-8">Select Date</span>
                <div className="border border-[#E0E0DE] p-6 inline-block bg-white" data-testid="calendar-container">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={[{ before: new Date() }, { dayOfWeek: [0, 6] }]}
                    className="rounded-none"
                  />
                </div>
                
                {selectedDate && (
                  <p className="mt-6 text-sm">
                    <span className="text-[#737373]">Selected:</span>{" "}
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                )}

                <div className="mt-12">
                  <span className="label-caps block mb-6">Preferred Time</span>
                  <div className="flex flex-wrap gap-2" data-testid="time-slots">
                    {timeSlots.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setFormData(p => ({ ...p, preferred_time: time }))}
                        data-testid={`time-slot-${time}`}
                        className={`py-3 px-5 border text-sm transition-all ${
                          formData.preferred_time === time
                            ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                            : "border-[#E0E0DE] hover:border-[#0A0A0A]"
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Details */}
              <div>
                <span className="label-caps block mb-8">Your Details</span>

                <div className="space-y-6">
                  <div>
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      data-testid="appointment-name-input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      data-testid="appointment-email-input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+90 xxx xxx xxxx"
                      data-testid="appointment-phone-input"
                    />
                  </div>

                  <div>
                    <label className="form-label">Project Type</label>
                    <select
                      name="project_type"
                      value={formData.project_type}
                      onChange={handleChange}
                      required
                      data-testid="appointment-project-type-select"
                    >
                      <option value="">Select type</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Notes</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Brief description of your project..."
                      data-testid="appointment-notes-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || !selectedDate || !formData.preferred_time}
                    className="btn-editorial w-full justify-center disabled:opacity-50"
                    data-testid="appointment-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Request Appointment
                        <Send size={16} />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-[#737373] text-center">
                    We'll confirm your appointment within 24 hours
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};
